import {compact} from '@benchling/js-utils';
import React, {useEffect, useReducer} from 'react';
import isEqual from 'react-fast-compare';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {MonomerNaturalAnalogue} from 'graphql-client/__generated/graphqlGlobalTypes';
import {MonomerQuery} from 'molbio/monomers/__generated/MonomerQuery';
import {SelfOpeningMonomerDropdown, SpecialMonomerValue} from 'molbio/monomers/MonomerDropdown';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import ErrorMessage from 'ui-library/ErrorMessage';
import {Size} from 'ui-library/inputs/InputConstants';
import TextInput from 'ui-library/inputs/TextInput';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import pickDefined from 'util/pickDefined';
import {InputChangeEvent} from 'util/Types';

import {MonomerFilter, MonomerFilterQualifier, Operator} from '../SearchConstants';
import {BASE_FILTER_FORM_PROP_NAMES, BaseFilterFormProps, FilterStyle} from './base/FilterFormConstants';
import OperatorDropdown from './base/OperatorDropdown';
import SecondaryFilterFormSkeleton from './base/SecondaryFilterFormSkeleton';
import localFilterStateReducer, {
  MonomerLocalFilterAction,
  MonomerLocalFilterActionType,
} from './monomerFilterFormReducer';

type Monomer = MonomerQuery['monomers'][number];

const QUALIFIER_TO_DISPLAY_NAME: {[q in MonomerFilterQualifier]: string} = {
  [MonomerFilterQualifier.ANY_POS]: 'at any position',
  [MonomerFilterQualifier.AT_POS]: 'at position(s)',
  [MonomerFilterQualifier.MIN_COUNT]: 'minimum count',
  [MonomerFilterQualifier.MAX_COUNT]: 'maximum count',
};

function QualifierDropdown({
  operator,
  qualifier,
  dispatch,
}: {
  operator: Operator;
  qualifier: MonomerFilterQualifier;
  dispatch: React.Dispatch<MonomerLocalFilterAction>;
}): JSX.Element {
  const menu = (
    <Menu width={MenuWidth.FULL} value={qualifier}>
      {[
        MonomerFilterQualifier.ANY_POS,
        MonomerFilterQualifier.AT_POS,
        operator === Operator.IS && MonomerFilterQualifier.MIN_COUNT,
        operator === Operator.IS && MonomerFilterQualifier.MAX_COUNT,
      ]
        .filter((q) => Boolean(q))
        .map((qualifier: MonomerFilterQualifier) => (
          <Option
            key={qualifier}
            value={qualifier}
            onSelect={(qualifier: MonomerFilterQualifier) => {
              dispatch({type: MonomerLocalFilterActionType.UPDATE_QUALIFIER, qualifier});
            }}
          >
            {QUALIFIER_TO_DISPLAY_NAME[qualifier]}
          </Option>
        ))}
    </Menu>
  );
  return (
    <Dropdown menu={menu}>
      <Button
        isDropdown={true}
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        size={ButtonSize.SMALL}
        fullWidth={true}
        text={QUALIFIER_TO_DISPLAY_NAME[qualifier]}
      />
    </Dropdown>
  );
}

const qualifierValueToText = (value: number | Array<number> | undefined, valIsArray: boolean): string =>
  value === undefined
    ? ''
    : valIsArray
      ? (value as Array<number>).map((n) => n + 1).join(', ')
      : value.toString();

function QualifierValueInput({
  error,
  textValue,
  dispatch,
  valIsArray,
}: {
  error: string;
  textValue: string;
  dispatch: React.Dispatch<MonomerLocalFilterAction>;
  valIsArray: boolean;
}): JSX.Element {
  return (
    <>
      <TextInput
        hasError={error.length > 0}
        onChange={(e: InputChangeEvent) => {
          dispatch({textValue: e.target.value, type: MonomerLocalFilterActionType.UPDATE_QUALIFIER_VALUE});
        }}
        size={Size.SMALL}
        value={textValue}
        placeholder={valIsArray ? 'Enter comma-separated positions' : 'Enter count'}
      />
      {error.length > 0 && <ErrorMessage error={error} />}
    </>
  );
}

export interface MonomerFilterFormProps extends BaseFilterFormProps {
  filter: MonomerFilter;
  filterStyle: FilterStyle;
  onChangeFilter: (filter: MonomerFilter) => void;
  onRemove: () => void;
  operators: Array<Operator>;
}

export default function MonomerFilterForm(props: MonomerFilterFormProps): JSX.Element {
  assert(props.filterStyle === FilterStyle.SECONDARY, 'Monomer filter only supported as SECONDARY');
  const {onChangeFilter, filter} = props;
  const skeletonProps = pickDefined(props, ...BASE_FILTER_FORM_PROP_NAMES);
  const [localFilterState, dispatch] = useReducer(localFilterStateReducer, {
    filter,
    qualifierValueError: '',
    qualifierValueText: qualifierValueToText(
      filter.qualifierValue,
      filter.qualifier === MonomerFilterQualifier.AT_POS
    ),
  });

  useEffect(() => {
    if (!isEqual(localFilterState.filter, filter)) {
      onChangeFilter(localFilterState.filter);
    }
  }, [localFilterState.filter, filter, onChangeFilter]);

  const operatorDropdown = (
    <OperatorDropdown
      buttonSize={props.buttonSize}
      onChangeOperator={(operator: Operator) => {
        dispatch({type: MonomerLocalFilterActionType.UPDATE_OPERATOR, operator});
      }}
      operators={props.operators}
      selectedOperator={props.filter.operator}
    />
  );
  const acceptedNaturalMonomers = [MonomerNaturalAnalogue.D_LOWER, MonomerNaturalAnalogue.R_LOWER];
  if (BenchlingConfig.MOLBIO_ENABLE_UT_FLEXIBILITY) {
    acceptedNaturalMonomers.push(...[MonomerNaturalAnalogue.T, MonomerNaturalAnalogue.U]);
  }
  const monomerDropdown = (
    <SelfOpeningMonomerDropdown
      allowArchived={true}
      selectedMonomerId={props.filter.value}
      updateSelectedMonomerId={(newId: string) => {
        dispatch({type: MonomerLocalFilterActionType.UPDATE_VALUE, value: newId});
      }}
      fullWidth={true}
      nameMaxLength={50}
      allowedSpecialValues={[SpecialMonomerValue.ANY_MODIFIED]}
      filterMonomersPredicate={(mon: Monomer) =>
        !mon.isNatural || acceptedNaturalMonomers.includes(mon.naturalAnalogue)
      }
    />
  );
  const qualifierDropdown = (
    <QualifierDropdown
      operator={props.filter.operator}
      qualifier={props.filter.qualifier}
      dispatch={dispatch}
    />
  );
  const qualifierValueInput =
    props.filter.qualifier === MonomerFilterQualifier.ANY_POS ? null : (
      <QualifierValueInput
        error={localFilterState.qualifierValueError}
        textValue={localFilterState.qualifierValueText}
        dispatch={dispatch}
        valIsArray={props.filter.qualifier === MonomerFilterQualifier.AT_POS}
      />
    );
  return (
    <SecondaryFilterFormSkeleton
      {...skeletonProps}
      nodeGrid={compact([
        [operatorDropdown],
        [monomerDropdown],
        [qualifierDropdown],
        qualifierValueInput && [qualifierValueInput],
      ])}
      onRemove={props.onRemove}
    />
  );
}
